import React, { useCallback, useRef, useState } from 'react'

function assignRef(ref: React.Ref<unknown> | undefined, node: unknown) {
    if (typeof ref === 'function') {
        ref(node)
    } else if (ref) {
        ;(ref as React.MutableRefObject<unknown>).current = node
    }
}

/**
 * Measures a Popover trigger so the floating panel can be laid out at least as
 * wide as the trigger instead of at a fixed `min-w`.
 *
 * `measureTriggerRef` takes the ref that Popover injects into its trigger and
 * returns a callback ref that forwards it *and* records the measured width —
 * the trigger cannot simply be given two refs. Measurement is
 * `getBoundingClientRect`, so `triggerWidth` stays 0 on native and the caller
 * falls back to its own `min-w`.
 */
export function useMeasuredTriggerWidth() {
    const [triggerWidth, setTriggerWidth] = useState(0)
    const measuredWidthRef = useRef(0)

    const measureTriggerRef = useCallback(
        (popoverRef: React.Ref<unknown> | undefined): React.RefCallback<unknown> =>
            node => {
                assignRef(popoverRef, node)
                const rect = (
                    node as { getBoundingClientRect?: () => { width: number } } | null
                )?.getBoundingClientRect?.()
                const width = rect?.width ? Math.round(rect.width) : 0
                if (width && width !== measuredWidthRef.current) {
                    measuredWidthRef.current = width
                    setTriggerWidth(width)
                }
            },
        [],
    )

    return { triggerWidth, measureTriggerRef }
}
